import { useCallback, useEffect, useId, useLayoutEffect, useState, type RefObject } from 'react';
import { Link } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { GAME_SECTIONS, type GameId, type GameSection } from '@tg/shared';
import { paths } from '@/app/paths';
import { GameCover } from '@/art/GameCover';
import { useLocale } from '@/i18n/useLocale';
import { useFormat } from '@/i18n/useFormat';
import { cx } from '@/ui/cx';
import { buttonClasses } from '@/ui/Button';
import { useContinue } from '@/skill/continue';
import { useJackpot } from '@/features/jackpot/store';
import { JackpotBanner } from '@/features/jackpot/JackpotBanner';
import { useQuest } from '@/features/quests/store';
import { QuestCard } from '@/features/quests/QuestCard';
import { SeasonBanner } from '@/features/season/SeasonDecor';
import { useSeason } from '@/features/season/useSeason';
import { useGameName } from './parts';
import s from './lobby.module.css';

/**
 * The catalog controls of the lobby (docs/four-games-mobile-plan.md «Мобильный обзор»): section filter chips, the
 * remembered filter and scroll position, «Continue» and the one compact events block. Per tab, in sessionStorage:
 * coming back from a game restores the filter and where the list was; every storage access is guarded.
 */
export type LobbyFilter = 'all' | GameSection;
export const LOBBY_FILTERS: readonly LobbyFilter[] = ['all', ...GAME_SECTIONS];

const FILTER_KEY = 'tg.lobby.filter';
const SCROLL_KEY = 'tg.lobby.scroll';

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeSession(key: string, v: string): void {
  try {
    sessionStorage.setItem(key, v);
  } catch {
    /* storage blocked: nothing is remembered */
  }
}

const isFilter = (x: string | null): x is LobbyFilter => !!x && (LOBBY_FILTERS as readonly string[]).includes(x);

/** the selected section (remembered for the tab) */
export function useLobbyFilter(): [LobbyFilter, (f: LobbyFilter) => void] {
  const [f, setF] = useState<LobbyFilter>(() => {
    const v = readSession(FILTER_KEY);
    return isFilter(v) ? v : 'all';
  });
  const set = useCallback((next: LobbyFilter) => {
    setF(next);
    writeSession(FILTER_KEY, next);
  }, []);
  return [f, set];
}

/** is a section shown under the filter */
export const shows = (f: LobbyFilter, section: GameSection): boolean => f === 'all' || f === section;

/** Keeps the scroll position of the lobby's scroll box across visits of a game (restored once on mount). */
export function useScrollMemory(ref: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const y = Number(readSession(SCROLL_KEY));
    if (Number.isFinite(y) && y > 0) el.scrollTop = y;
    let frame = 0;
    const save = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        writeSession(SCROLL_KEY, String(Math.round(el.scrollTop)));
      });
    };
    el.addEventListener('scroll', save, { passive: true });
    return () => {
      el.removeEventListener('scroll', save);
      if (frame) cancelAnimationFrame(frame);
      writeSession(SCROLL_KEY, String(Math.round(el.scrollTop)));
    };
  }, [ref]);
}

/** Horizontal, scrollable section chips: All, Puzzles, Strategy, Originals, Tables, Slots, Machines. */
export function FilterChips({ value, onChange, className }: { value: LobbyFilter; onChange: (f: LobbyFilter) => void; className?: string }) {
  const intl = useIntl();
  return (
    <div className={cx(s.filters, className)} role="group" aria-label={intl.formatMessage({ id: 'lobby.filter.label' })}>
      {LOBBY_FILTERS.map((f) => (
        <button
          key={f}
          type="button"
          className={cx(s.chip, value === f && s.chipOn)}
          aria-pressed={value === f}
          data-filter={f}
          onClick={() => onChange(f)}
        >
          <FormattedMessage id={f === 'all' ? 'lobby.filter.all' : `common.section.${f}`} />
        </button>
      ))}
    </div>
  );
}

/** The search box of the phone catalog: a real input (16 px — no iOS zoom), ≥ 44 px tall. */
export function CatalogSearch({ value, onChange }: { value: string; onChange: (q: string) => void }) {
  const intl = useIntl();
  const label = intl.formatMessage({ id: 'lobby.search' });
  return (
    <input
      type="search"
      className={s.mSearch}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={label}
      aria-label={label}
      enterKeyHint="search"
      autoComplete="off"
    />
  );
}

/** «Continue» at the top: an unfinished skill session, else the last game opened here; nothing on a first visit. */
export function ResumeBlock() {
  const item = useContinue();
  const l = useLocale();
  const name = useGameName();
  if (!item) return null;
  const game: GameId = item.game;
  return (
    <Link to={paths.game(l, game)} className={s.resume} data-testid="lobby-resume" data-game={game}>
      <GameCover game={game} width={64} height={40} className={s.resumeCover} />
      <span className={s.resumeText}>
        <span className={s.resumeTitle}>
          <FormattedMessage id="lobby.resume.title" />
        </span>
        <span className={s.resumeSub}>
          {name(game)} · <FormattedMessage id={item.kind === 'session' ? 'lobby.resume.session' : 'lobby.resume.last'} />
        </span>
      </span>
      <span className={cx(buttonClasses.btn, buttonClasses.teal, buttonClasses.solid, buttonClasses.sm, s.resumePlay)}>
        <FormattedMessage id="lobby.resume.play" />
      </span>
    </Link>
  );
}

/**
 * One compact block of what is on now — the daily quest, the jackpot, the season — folded into a single line;
 * «Show» opens the full blocks below it. Nothing on → no block.
 */
export function EventsBlock() {
  const quest = useQuest((q) => q.quest);
  const jp = useJackpot();
  const season = useSeason();
  const f = useFormat();
  const [open, setOpen] = useState(false);
  const panel = useId();
  // a claimable quest reward opens the block by itself once: nothing waiting is hidden behind «Show»
  const complete = quest?.status === 'complete';
  useEffect(() => {
    if (complete) setOpen(true);
  }, [complete]);
  if (!quest && !jp && !season) return null;
  return (
    <section className={s.events} data-testid="lobby-events">
      <button type="button" className={s.eventsHead} aria-expanded={open} aria-controls={panel} onClick={() => setOpen((o) => !o)}>
        <span className={s.eventsTitle}>
          <FormattedMessage id="lobby.events.title" />
        </span>
        <span className={s.eventsSummary}>
          {quest && (
            <span className={s.eventsItem}>
              <FormattedMessage id="quests.title" /> {f.int(Math.min(quest.done, quest.target))}/{f.int(quest.target)}
            </span>
          )}
          {jp && (
            <span className={s.eventsItem}>
              <FormattedMessage id="jackpot.title" /> {f.tokens(jp.pool, { decimals: 0 })}
            </span>
          )}
          {season && (
            <span className={s.eventsItem}>
              <FormattedMessage id={`lobby.season.${season.id}.title`} />
            </span>
          )}
        </span>
        <span className={s.eventsToggle}>
          <FormattedMessage id={open ? 'lobby.events.less' : 'lobby.events.more'} />
        </span>
      </button>
      <div id={panel} className={s.eventsBody} hidden={!open}>
        {open && (
          <>
            <QuestCard compact />
            <JackpotBanner compact />
            {season && <SeasonBanner season={season} />}
          </>
        )}
      </div>
    </section>
  );
}
